import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Braces, FileText, Pencil, Plus, RefreshCw, Send, Trash2 } from "lucide-react";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Textarea } from "../../ui/textarea";
import WhatsAppProvaInvio from "./WhatsAppProvaInvio";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/admin/crm/whatsapp`;
const POLL_MS = 30000;

const GRADIENT = "border-0 bg-emerald-600 text-white hover:bg-emerald-700";

const LANGUAGES = [
  { value: "it", label: "Italiano" },
  { value: "en", label: "Inglese" },
  { value: "en_US", label: "Inglese (USA)" },
  { value: "es", label: "Spagnolo" },
  { value: "fr", label: "Francese" },
  { value: "de", label: "Tedesco" },
];

const CATEGORIES = [
  { value: "UTILITY", label: "Servizio", hint: "Conferme, promemoria e aggiornamenti su una pratica o un appuntamento." },
  { value: "MARKETING", label: "Marketing", hint: "Promozioni e offerte. Il destinatario deve aver dato il consenso." },
];

const CATEGORY_LABELS = { UTILITY: "Servizio", MARKETING: "Marketing", AUTHENTICATION: "Autenticazione" };

const STATUS = {
  APPROVED: { label: "Approvato", className: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500" },
  PENDING: { label: "In revisione", className: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  REJECTED: { label: "Rifiutato", className: "bg-rose-100 text-rose-800", dot: "bg-rose-500" },
  PAUSED: { label: "In pausa", className: "bg-orange-100 text-orange-800", dot: "bg-orange-500" },
  DISABLED: { label: "Disattivato", className: "bg-slate-200 text-slate-700", dot: "bg-slate-500" },
  IN_APPEAL: { label: "In appello", className: "bg-sky-100 text-sky-800", dot: "bg-sky-500" },
};

const STATUS_NOTES = {
  APPROVED: "Approvato da Meta: si può usare per scrivere ai destinatari.",
  PENDING: "Meta sta revisionando il template. Lo stato si aggiorna da solo appena arriva la risposta.",
  REJECTED: "Meta ha rifiutato il template. Correggilo e invialo di nuovo con un altro nome.",
  PAUSED: "Meta ha messo in pausa il template per bassa qualità: non si può inviare finché non torna attivo.",
  DISABLED: "Meta ha disattivato il template: non si può più inviare.",
  IN_APPEAL: "Il rifiuto è in appello presso Meta.",
};

const EMPTY = {
  id: null,
  meta_id: null,
  editableMeta: false,
  meta_status: "",
  name: "",
  category: "UTILITY",
  language: "it",
  header_text: "",
  header_document: false,
  header_image: false,
  header_image_file: null,
  header_image_name: "",
  body: "",
  footer_text: "",
  buttons: ["", "", ""],
  examples: {},
  bindings: {},
};

const DEFAULT_VARIABLE_FIELDS = [
  { id: "lead.nome_ospite", group: "Lead", label: "Nome ospite", example: "Mario" },
  { id: "lead.cognome_ospite", group: "Lead", label: "Cognome ospite", example: "Rossi" },
  { id: "lead.eta", group: "Lead", label: "Età", example: "85" },
  { id: "lead.nome_referente", group: "Lead", label: "Nome referente", example: "Anna" },
  { id: "lead.telefono_referente", group: "Lead", label: "Telefono referente", example: "3331234567" },
  { id: "struttura.nome", group: "Struttura", label: "Nome struttura", example: "Residenza Althea" },
  { id: "struttura.nome_referente", group: "Struttura", label: "Nome referente", example: "Giampaolo" },
  { id: "struttura.telefono_referente", group: "Struttura", label: "Tel. referente", example: "3331234567" },
  { id: "struttura.comune", group: "Struttura", label: "Comune", example: "Roma" },
];

function fieldLabel(fields, id) {
  const found = (fields || []).find((item) => item.id === id);
  return found ? `${found.group} · ${found.label}` : id || "Seleziona campo";
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Impossibile leggere l'immagine"));
    reader.readAsDataURL(file);
  });
}

function formatWhen(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("it-IT", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function languageLabel(code) {
  return LANGUAGES.find((item) => item.value === code)?.label || code || "";
}

function variablesOf(text) {
  return [...new Set((String(text || "").match(/\{\{(\d+)\}\}/g) || []).map((item) => Number(item.replace(/\D/g, ""))))]
    .sort((a, b) => a - b);
}

/** Legge i componenti di un template Meta e li porta in un formato unico per l'anteprima. */
function previewOf(template) {
  const components = template.components || [];
  const find = (type) => components.find((item) => String(item.type).toUpperCase() === type);
  const header = find("HEADER");
  const body = find("BODY");
  const footer = find("FOOTER");
  const buttons = find("BUTTONS");
  const examples = body?.example?.body_text?.[0] || [];
  return {
    header: header ? (String(header.format || "TEXT").toUpperCase() === "TEXT" ? header.text : "") : "",
    headerDocument: Boolean(header && String(header.format || "TEXT").toUpperCase() === "DOCUMENT"),
    headerMedia: header && !["TEXT", "DOCUMENT"].includes(String(header.format || "TEXT").toUpperCase())
      ? String(header.format).toLowerCase() : "",
    body: body?.text || "",
    examples,
    footer: footer?.text || "",
    buttons: (buttons?.buttons || []).map((item) => item.text).filter(Boolean),
  };
}

function fillVariables(text, examples) {
  return String(text || "").replace(/\{\{(\d+)\}\}/g, (match, number) => examples?.[Number(number) - 1] || match);
}

/** Fumetto WhatsApp su sfondo beige, usato sia nelle card sia nell'editor. */
function ChatPreview({ header, headerDocument, headerMedia, headerImageUrl, body, examples, footer, buttons }) {
  const text = fillVariables(body, examples);
  return (
    <div className="rounded-xl bg-[#efe7dd] p-3">
      <div className="max-w-full rounded-lg rounded-tl-none bg-white p-2.5 text-sm shadow-sm">
        {headerDocument && (
          <div className="mb-2 flex items-center gap-2 rounded bg-slate-100 p-2 text-xs text-slate-600">
            <FileText className="h-4 w-4" /> Documento PDF allegato
          </div>
        )}
        {(headerImageUrl || headerMedia === "image") && (
          headerImageUrl
            ? <img src={headerImageUrl} alt="Intestazione" className="mb-2 max-h-36 w-full rounded object-cover" />
            : <div className="mb-2 rounded bg-slate-100 p-2 text-xs text-slate-600">Intestazione: immagine</div>
        )}
        {headerMedia && headerMedia !== "image" && <div className="mb-2 rounded bg-slate-100 p-2 text-xs text-slate-600">Intestazione: {headerMedia}</div>}
        {header && <p className="mb-1 font-semibold text-slate-900">{header}</p>}
        {text ? <p className="whitespace-pre-wrap break-words text-slate-800">{text}</p> : <p className="text-slate-400">Il testo del messaggio comparirà qui.</p>}
        {footer && <p className="mt-1.5 text-xs text-slate-500">{footer}</p>}
      </div>
      {buttons?.length > 0 && (
        <div className="mt-1 space-y-1">
          {buttons.map((label) => (
            <div key={label} className="rounded-lg bg-white py-1.5 text-center text-sm font-medium text-sky-600 shadow-sm">{label}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const info = STATUS[status] || { label: status || "Sconosciuto", className: "bg-slate-100 text-slate-700", dot: "bg-slate-400" };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${info.className}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${info.dot}`} /> {info.label}
    </span>
  );
}

function TemplateCard({ template, onSend, onEdit }) {
  const preview = previewOf(template);
  const bindings = template.affidya?.variable_bindings || [];
  const canEdit = ["APPROVED", "REJECTED", "PAUSED"].includes(String(template.status || "").toUpperCase());
  return (
    <Card className="overflow-hidden rounded-2xl">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-mono text-sm font-semibold">{template.name}</p>
            <p className="text-xs text-slate-500">{languageLabel(template.language)} · {CATEGORY_LABELS[template.category] || template.category}</p>
          </div>
          <StatusBadge status={template.status} />
        </div>
        <ChatPreview {...preview} />
        {bindings.length > 0 && (
          <p className="text-[11px] text-slate-500">
            Campi Affidya: {bindings.map((item, index) => `{{${index + 1}}}=${item}`).join(" · ")}
          </p>
        )}
        <p className="text-xs text-slate-600">
          {STATUS_NOTES[template.status] || "Stato non riconosciuto."}
          {template.status === "REJECTED" && template.rejected_reason && template.rejected_reason !== "NONE" ? ` Motivo: ${template.rejected_reason}.` : ""}
        </p>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-[11px] text-slate-400">{template.aggiornato_at ? `Aggiornato ${formatWhen(template.aggiornato_at)}` : ""}</span>
          <div className="flex gap-1">
            {canEdit && (
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => onEdit(template)}>
                <Pencil className="mr-1 h-3.5 w-3.5" /> Modifica
              </Button>
            )}
            {template.status === "APPROVED" && (
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => onSend(template)}>
                <Send className="mr-1 h-3.5 w-3.5" /> Invia
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DraftCard({ draft, onEdit, onDelete }) {
  return (
    <Card className="rounded-2xl border-dashed">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-mono text-sm font-semibold">{draft.name || "senza_nome"}</p>
            <p className="text-xs text-slate-500">{languageLabel(draft.language)} · {CATEGORY_LABELS[draft.category] || draft.category}</p>
          </div>
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">Bozza</span>
        </div>
        <ChatPreview
          header={draft.header_text}
          headerDocument={draft.header_document}
          headerMedia={draft.header_image ? "image" : ""}
          body={draft.body}
          examples={draft.body_examples}
          footer={draft.footer_text}
          buttons={(draft.buttons || []).filter(Boolean)}
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-400">{draft.updated_at ? `Salvata ${formatWhen(draft.updated_at)}` : ""}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => onEdit(draft)}><Pencil className="mr-1 h-3.5 w-3.5" /> Modifica</Button>
            <Button size="sm" variant="ghost" className="rounded-full text-rose-600" onClick={() => onDelete(draft)} title="Elimina bozza"><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function fromDraft(draft) {
  const variables = variablesOf(draft.body);
  const examples = {};
  const bindings = {};
  variables.forEach((number, index) => {
    examples[number] = (draft.body_examples || [])[index] || "";
    bindings[number] = (draft.variable_bindings || [])[index] || "";
  });
  const buttons = [...(draft.buttons || []), "", "", ""].slice(0, 3);
  return {
    id: draft.id,
    meta_id: null,
    editableMeta: false,
    name: draft.name || "",
    category: draft.category || "UTILITY",
    language: draft.language || "it",
    header_text: draft.header_text || "",
    header_document: Boolean(draft.header_document),
    header_image: Boolean(draft.header_image),
    header_image_file: null,
    header_image_name: draft.header_image_path ? "Immagine già salvata in bozza" : "",
    body: draft.body || "",
    footer_text: draft.footer_text || "",
    buttons,
    examples,
    bindings,
  };
}

function fromMetaTemplate(template) {
  const preview = previewOf(template);
  const variables = variablesOf(preview.body);
  const examples = {};
  const bindings = {};
  const affidyaBindings = template.affidya?.variable_bindings || [];
  variables.forEach((number, index) => {
    examples[number] = preview.examples?.[index] || "";
    bindings[number] = affidyaBindings[index] || "";
  });
  const headerKind = String(template.affidya?.header_kind || "").toLowerCase();
  const headerImage = Boolean(preview.headerMedia === "image" || headerKind === "image" || template.affidya?.has_header_image);
  const headerDocument = Boolean(preview.headerDocument || headerKind === "document");
  return {
    id: null,
    meta_id: template.id || "",
    editableMeta: true,
    meta_status: String(template.status || "").toUpperCase(),
    name: template.name || "",
    category: template.category || "UTILITY",
    language: template.language || "it",
    header_text: preview.header || "",
    header_document: headerDocument,
    header_image: headerImage,
    header_image_file: null,
    header_image_name: headerImage ? "Immagine già presente sul template" : "",
    body: preview.body || "",
    footer_text: preview.footer || "",
    buttons: [...(preview.buttons || []), "", "", ""].slice(0, 3),
    examples,
    bindings,
  };
}

function TemplateEditor({ open, onOpenChange, initial, headers, variableFields, onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const bodyRef = useRef(null);
  const fields = variableFields?.length ? variableFields : DEFAULT_VARIABLE_FIELDS;
  const editingMeta = Boolean(form.meta_id && form.editableMeta);
  const categoryLocked = editingMeta && form.meta_status === "APPROVED";

  useEffect(() => {
    if (!open) return;
    setForm({
      ...EMPTY,
      ...(initial || {}),
      examples: { ...(initial?.examples || {}) },
      bindings: { ...(initial?.bindings || {}) },
      buttons: [...(initial?.buttons || ["", "", ""])].slice(0, 3),
    });
  }, [open, initial]);
  useEffect(() => {
    if (!form.header_image_file) {
      setPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(form.header_image_file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [form.header_image_file]);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const variables = useMemo(() => variablesOf(form.body), [form.body]);
  const cleanButtons = form.buttons.map((item) => item.trim()).filter(Boolean);
  const examples = variables.map((number) => form.examples[number] || "");
  const bindings = variables.map((number) => form.bindings[number] || "");

  const addVariable = () => {
    const next = (variables.length ? Math.max(...variables) : 0) + 1;
    const token = `{{${next}}}`;
    const element = bodyRef.current;
    const start = element?.selectionStart ?? form.body.length;
    const end = element?.selectionEnd ?? form.body.length;
    set({ body: `${form.body.slice(0, start)}${token}${form.body.slice(end)}` });
    requestAnimationFrame(() => {
      if (!element) return;
      element.focus();
      element.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const setBinding = (number, fieldId) => {
    const field = fields.find((item) => item.id === fieldId);
    set({
      bindings: { ...form.bindings, [number]: fieldId },
      examples: {
        ...form.examples,
        [number]: form.examples[number] || field?.example || "",
      },
    });
  };

  const validate = () => {
    if (!editingMeta && !/^[a-z0-9_]+$/.test(form.name.trim())) {
      return "Il nome può contenere solo lettere minuscole, numeri e trattino basso.";
    }
    const text = form.body.trim();
    if (!text) return "Scrivi il testo del messaggio.";
    if (/^\{\{\d+\}\}/.test(text) || /\{\{\d+\}\}$/.test(text)) return "Il testo non può iniziare o finire con una variabile.";
    const all = (text.match(/\{\{(\d+)\}\}/g) || []).map((item) => Number(item.replace(/\D/g, "")));
    if (all.some((number, index) => number !== index + 1)) return "Le variabili devono essere {{1}}, {{2}} e così via, in ordine.";
    if (examples.some((item) => !item.trim())) return "Indica un esempio per ogni variabile.";
    if (bindings.some((item) => !item)) return "Associa un campo Affidya a ogni variabile.";
    if (form.header_image && !form.header_image_file && !form.header_image_name) return "Carica un'immagine per l'intestazione.";
    if (form.header_document && form.header_image) return "Scegli un solo tipo di intestazione.";
    if (editingMeta && !form.meta_id) return "Identificativo Meta del template mancante.";
    const longButton = cleanButtons.find((item) => item.length > 35);
    if (longButton) {
      return `La risposta rapida «${longButton}» ha ${longButton.length} caratteri (massimo 35).`;
    }
    return "";
  };

  const imagePayload = async () => {
    if (!form.header_image || !form.header_image_file) {
      return { header_image_base64: "", header_image_mime: "" };
    }
    const dataUrl = await readFileAsBase64(form.header_image_file);
    return {
      header_image_base64: dataUrl,
      header_image_mime: form.header_image_file.type || "image/jpeg",
    };
  };

  const draftPayload = async () => {
    const image = await imagePayload();
    return {
      id: form.id,
      name: form.name.trim(),
      category: form.category,
      language: form.language,
      body: form.body,
      body_examples: examples,
      variable_bindings: bindings,
      header_text: (form.header_document || form.header_image) ? "" : form.header_text,
      header_document: form.header_document,
      header_image: form.header_image,
      ...image,
      footer_text: form.footer_text,
      buttons: form.buttons,
    };
  };

  const request = async (path, payload, method = "POST") => {
    const res = await fetch(`${BASE}${path}`, { method, headers, body: JSON.stringify(payload) });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = json.detail;
      const message = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((item) => item?.msg || JSON.stringify(item)).join(" ")
          : "Operazione non riuscita";
      throw new Error(message);
    }
    return json;
  };

  const saveDraft = async () => {
    if (editingMeta) return;
    setBusy("draft");
    try {
      await request("/templates/bozze", await draftPayload());
      toast.success("Bozza salvata");
      onOpenChange(false);
      onDone();
    } catch (error) { toast.error(error.message); } finally { setBusy(""); }
  };

  const submit = async () => {
    const problem = validate();
    if (problem) return toast.error(problem);
    setBusy("send");
    try {
      const image = await imagePayload();
      const shared = {
        category: form.category,
        body: form.body.trim(),
        body_examples: examples.map((item) => item.trim()),
        variable_bindings: bindings,
        header_text: (form.header_document || form.header_image) ? "" : form.header_text.trim(),
        header_document: form.header_document,
        header_image: form.header_image,
        ...image,
        footer_text: form.footer_text.trim(),
        buttons: cleanButtons,
      };
      if (editingMeta) {
        await request(`/templates/${encodeURIComponent(form.meta_id)}`, shared);
        toast.success("Template aggiornato e reinviato a Meta per la verifica.");
      } else {
        await request("/templates", {
          ...shared,
          name: form.name.trim(),
          language: form.language,
          bozza_id: form.id,
        });
        toast.success("Template inviato a Meta. Lo stato iniziale è In revisione.");
      }
      onOpenChange(false);
      onDone();
    } catch (error) { toast.error(error.message); } finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {editingMeta ? "Modifica template" : form.id ? "Modifica bozza" : "Nuovo template"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
          <div className="space-y-4 text-sm">
            {editingMeta && (
              <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                Stai modificando un template già su Meta. Nome e lingua restano fissi; dopo il salvataggio Meta lo rimette in revisione
                (limite: 1 modifica ogni 24 ore, fino a 10 in 30 giorni se è approvato).
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Nome</Label>
                <Input
                  className="font-mono"
                  value={form.name}
                  disabled={editingMeta}
                  placeholder="promemoria_pratica"
                  onChange={(event) => set({ name: event.target.value.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "") })}
                />
                <p className="mt-1 text-xs text-slate-500">
                  {editingMeta ? "Il nome non si può cambiare su un template esistente." : "Solo lettere minuscole, numeri e trattino basso."}
                </p>
              </div>
              <div>
                <Label>Lingua</Label>
                <Select value={form.language} onValueChange={(value) => set({ language: value })} disabled={editingMeta}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGUAGES.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Categoria</Label>
              <div className="mt-1 grid gap-2 sm:grid-cols-2">
                {CATEGORIES.map((item) => {
                  const active = form.category === item.value;
                  return (
                    <button
                      type="button"
                      key={item.value}
                      disabled={categoryLocked}
                      onClick={() => set({ category: item.value })}
                      className={`flex items-start gap-3 rounded-xl border p-3 text-left transition ${active ? "border-emerald-500 bg-emerald-50" : "hover:bg-slate-50"} ${categoryLocked ? "opacity-70" : ""}`}
                    >
                      <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${active ? "border-emerald-600" : "border-slate-300"}`}>
                        {active && <span className="h-2 w-2 rounded-full bg-emerald-600" />}
                      </span>
                      <span><span className="block font-medium">{item.label}</span><span className="text-xs text-slate-500">{item.hint}</span></span>
                    </button>
                  );
                })}
              </div>
              {categoryLocked && <p className="mt-1 text-xs text-slate-500">Su un template già approvato Meta non consente di cambiare la categoria.</p>}
            </div>

            <div>
              <Label>Intestazione <span className="font-normal text-slate-400">(facoltativa)</span></Label>
              <div className="mt-1 grid gap-2 sm:grid-cols-3">
                {[
                  { id: "text", label: "Testo" },
                  { id: "document", label: "PDF" },
                  { id: "image", label: "Immagine" },
                ].map((option) => {
                  const active = option.id === "document"
                    ? form.header_document
                    : option.id === "image"
                      ? form.header_image
                      : !form.header_document && !form.header_image;
                  return (
                    <button
                      type="button"
                      key={option.id}
                      className={`rounded-xl border px-3 py-2 text-left text-sm ${active ? "border-emerald-500 bg-emerald-50" : "hover:bg-slate-50"}`}
                      onClick={() => set({
                        header_document: option.id === "document",
                        header_image: option.id === "image",
                        header_text: option.id === "text" ? form.header_text : "",
                        header_image_file: option.id === "image" ? form.header_image_file : null,
                        header_image_name: option.id === "image" ? form.header_image_name : "",
                      })}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              {!form.header_document && !form.header_image && (
                <Input
                  className="mt-2"
                  maxLength={60}
                  value={form.header_text}
                  placeholder="Titolo in grassetto sopra il messaggio"
                  onChange={(event) => set({ header_text: event.target.value })}
                />
              )}
              {form.header_document && (
                <p className="mt-2 text-xs text-slate-500">
                  Meta chiede un PDF di esempio per la revisione: Affidya ne carica uno automaticamente. All&apos;invio reale dovrai allegare il documento.
                </p>
              )}
              {form.header_image && (
                <div className="mt-2 space-y-1">
                  <Input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      set({
                        header_image_file: file,
                        header_image_name: file?.name || form.header_image_name || "",
                      });
                    }}
                  />
                  {(form.header_image_name || previewUrl) && (
                    <p className="text-xs text-slate-500">
                      {form.header_image_name || "Immagine selezionata"}
                      {editingMeta && !form.header_image_file ? " (verrà riusata quella già salvata se non ne carichi una nuova)" : ""}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Testo del messaggio</Label>
                <Button type="button" size="sm" variant="outline" className="h-7 rounded-full" onClick={addVariable}>
                  <Braces className="mr-1 h-3.5 w-3.5" /> Aggiungi variabile
                </Button>
              </div>
              <Textarea
                ref={bodyRef}
                rows={6}
                maxLength={1024}
                value={form.body}
                placeholder="Buongiorno, abbiamo una richiesta per {{1}} {{2}} di {{3}} anni presso {{4}}."
                onChange={(event) => set({ body: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-500">
                Usa {"{{1}}"}, {"{{2}}"}… per le parti che cambiano. Poi associa ogni variabile a un campo del lead o della struttura.
              </p>
            </div>

            {variables.length > 0 && (
              <div className="space-y-3 rounded-xl bg-slate-50 p-3">
                <p className="text-xs font-medium text-slate-600">Campo Affidya ed esempio per ogni variabile</p>
                {variables.map((number) => (
                  <div key={number} className="grid gap-2 sm:grid-cols-[auto_1fr_1fr] sm:items-center">
                    <span className="font-mono text-xs text-slate-500">{`{{${number}}}`}</span>
                    <Select value={form.bindings[number] || ""} onValueChange={(value) => setBinding(number, value)}>
                      <SelectTrigger><SelectValue placeholder="Campo Affidya" /></SelectTrigger>
                      <SelectContent>
                        {fields.map((item) => (
                          <SelectItem key={item.id} value={item.id}>{item.group} · {item.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={form.examples[number] || ""}
                      placeholder="Esempio per Meta"
                      onChange={(event) => set({ examples: { ...form.examples, [number]: event.target.value } })}
                    />
                  </div>
                ))}
                <p className="text-[11px] text-slate-500">
                  L&apos;esempio serve solo a Meta per la revisione. In invio Affidya userà il campo associato (lead/struttura).
                </p>
              </div>
            )}

            <div>
              <Label>Piè di pagina <span className="font-normal text-slate-400">(facoltativo)</span></Label>
              <Input maxLength={60} value={form.footer_text} placeholder="Testo piccolo in fondo, senza variabili" onChange={(event) => set({ footer_text: event.target.value })} />
            </div>

            <div>
              <Label>Risposte rapide <span className="font-normal text-slate-400">(facoltative, massimo 3)</span></Label>
              <div className="mt-1 grid gap-2 sm:grid-cols-3">
                {form.buttons.map((button, index) => (
                  <Input
                    key={index}
                    maxLength={35}
                    value={button}
                    placeholder={`Pulsante ${index + 1}`}
                    onChange={(event) => set({ buttons: form.buttons.map((item, itemIndex) => (itemIndex === index ? event.target.value : item)) })}
                  />
                ))}
              </div>
              <p className="mt-1 text-xs text-slate-500">Massimo 35 caratteri per pulsante.</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="mb-2 text-sm font-medium">Anteprima</p>
              <ChatPreview
                header={(form.header_document || form.header_image) ? "" : form.header_text}
                headerDocument={form.header_document}
                headerMedia={form.header_image ? "image" : ""}
                headerImageUrl={previewUrl}
                body={form.body}
                examples={examples}
                footer={form.footer_text}
                buttons={cleanButtons}
              />
              {variables.length > 0 && (
                <ul className="mt-2 space-y-1 text-[11px] text-slate-500">
                  {variables.map((number) => (
                    <li key={number}>{`{{${number}}}`} → {fieldLabel(fields, form.bindings[number])}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="space-y-2">
              <Button className={`w-full rounded-full ${GRADIENT}`} onClick={submit} disabled={Boolean(busy)}>
                <Send className="mr-2 h-4 w-4" />
                {busy === "send"
                  ? (editingMeta ? "Aggiornamento…" : "Invio…")
                  : (editingMeta ? "Salva e reinvia a Meta" : "Salva e invia a Meta")}
              </Button>
              {!editingMeta && (
                <Button variant="outline" className="w-full rounded-full" onClick={saveDraft} disabled={Boolean(busy)}>
                  {busy === "draft" ? "Salvataggio…" : "Salva bozza"}
                </Button>
              )}
              <button type="button" className="w-full text-center text-sm text-slate-500 hover:underline" onClick={() => onOpenChange(false)}>Annulla</button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function WhatsAppTemplatePanel({ headers }) {
  const [templates, setTemplates] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [variableFields, setVariableFields] = useState(DEFAULT_VARIABLE_FIELDS);
  const [warning, setWarning] = useState("");
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sendTemplate, setSendTemplate] = useState(null);
  const headersRef = useRef(headers);
  headersRef.current = headers;

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${BASE}/templates`, { headers: headersRef.current });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Impossibile leggere i template");
      setTemplates((json.templates || []).filter((t) => t.name !== "hello_world"));
      setDrafts(json.bozze || []);
      if (Array.isArray(json.variable_fields) && json.variable_fields.length) {
        setVariableFields(json.variable_fields);
      }
      setWarning(json.warning || "");
    } catch (error) {
      if (!silent) toast.error(error.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const hasPending = templates.some((item) => item.status === "PENDING" || item.status === "IN_APPEAL");
  useEffect(() => {
    if (!hasPending) return undefined;
    const timer = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(timer);
  }, [hasPending, load]);

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openDraft = (draft) => { setEditing(fromDraft(draft)); setEditorOpen(true); };
  const openMetaTemplate = (template) => { setEditing(fromMetaTemplate(template)); setEditorOpen(true); };

  const deleteDraft = async (draft) => {
    if (!window.confirm(`Eliminare la bozza «${draft.name || "senza nome"}»?`)) return;
    try {
      const res = await fetch(`${BASE}/templates/bozze/${encodeURIComponent(draft.id)}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("Bozza non eliminata");
      load(true);
    } catch (error) { toast.error(error.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl space-y-2 text-sm text-slate-600">
          <p>
            I template sono messaggi approvati da Meta. Servono per scrivere a qualcuno quando sono passate più di 24 ore
            dal suo ultimo messaggio, o per iniziare una conversazione.
          </p>
          <p className="rounded-xl border bg-slate-50 p-3 text-xs">
            Meta revisiona ogni template, di solito in pochi minuti. Per ogni variabile scegli il campo Affidya
            (lead o struttura) da usare all&apos;invio; puoi anche aggiungere un&apos;immagine in intestazione.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => load()} disabled={loading}>
            <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna stato
          </Button>
          <Button size="sm" className={`rounded-full ${GRADIENT}`} onClick={openNew}>
            <Plus className="mr-1 h-4 w-4" /> Nuovo template
          </Button>
        </div>
      </div>

      {warning && <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{warning}</p>}

      {drafts.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-700">Bozze <Badge variant="secondary">{drafts.length}</Badge></h3>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {drafts.map((draft) => <DraftCard key={draft.id} draft={draft} onEdit={openDraft} onDelete={deleteDraft} />)}
          </div>
        </section>
      )}

      <section className="space-y-2">
        {drafts.length > 0 && <h3 className="text-sm font-semibold text-slate-700">Template su Meta</h3>}
        {!templates.length ? (
          <Card className="rounded-2xl"><CardContent className="p-8 text-center text-sm text-slate-500">{loading ? "Caricamento template…" : "Nessun template sull'account WhatsApp."}</CardContent></Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {templates.map((template) => (
              <TemplateCard
                key={template.id || `${template.name}-${template.language}`}
                template={template}
                onSend={setSendTemplate}
                onEdit={openMetaTemplate}
              />
            ))}
          </div>
        )}
      </section>

      <TemplateEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        initial={editing}
        headers={headers}
        variableFields={variableFields}
        onDone={() => load(true)}
      />

      <Dialog open={Boolean(sendTemplate)} onOpenChange={(open) => { if (!open) setSendTemplate(null); }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Invia {sendTemplate?.name}</DialogTitle></DialogHeader>
          {sendTemplate && <WhatsAppProvaInvio headers={headers} templates={templates} fixedTemplate={sendTemplate} onSent={() => setSendTemplate(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
